import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { glossarioTerms } from '../../data/glossario';

interface ContextualGlossaryProps {
  /** Textos do artigo usados para detectar termos (título, resumo, blocos). */
  texts: (string | undefined | null)[];
  maxTerms?: number;
}

/**
 * Glossário contextual: detecta termos do glossário citados no artigo e
 * exibe explicação rápida com link para a página completa do termo.
 */
export const ContextualGlossary: React.FC<ContextualGlossaryProps> = ({ texts, maxTerms = 6 }) => {
  const detected = useMemo(() => {
    const haystack = texts
      .filter(Boolean)
      .join(' ')
      .replace(/<[^>]+>/g, ' ')
      .toLowerCase();

    if (!haystack) return [];

    return glossarioTerms
      .map((term) => {
        // "Bitcoin (BTC)" → keywords ["bitcoin", "btc"]
        const keywords = term.termo
          .toLowerCase()
          .split(/[()\/,]+/)
          .map((k) => k.trim())
          .filter((k) => k.length >= 3);
        const matched = keywords.some((keyword) =>
          new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(haystack),
        );
        return matched ? term : null;
      })
      .filter((t): t is (typeof glossarioTerms)[number] => t !== null)
      .slice(0, maxTerms);
  }, [texts, maxTerms]);

  if (detected.length === 0) return null;

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Termos citados neste artigo</h2>
      <p className="text-xs text-gray-400 mb-4">
        Explicações rápidas do nosso <Link to="/glossario" className="underline hover:text-blue-600">glossário cripto</Link>.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {detected.map((term) => (
          <Link
            key={term.slug}
            to={`/glossario/${term.slug}`}
            className="border border-gray-100 rounded-lg p-3 hover:border-blue-200 hover:bg-blue-50 transition"
          >
            <p className="font-semibold text-gray-900 text-sm mb-0.5">{term.termo}</p>
            <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{term.resumo}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};
